import React, { useContext, useState, useEffect } from "react"
import PaymentCenterDetails from "./PaymentCenterDetails"
import SavedCards from "./SavedCards"
import TransactionSection from "./TransactionSection"
import { Button } from "../../../../components/ui/button"
import { PaymentRequests } from "../../../../api/app.service"
import {
  SvgFilledInformationCircle,
  SvgBilling,
  SvgWallet,
} from "../../../../components/icons"
import { useIntercom } from "react-use-intercom"
import { useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { UserCtx } from "../../../../context/userContext"
import { useToast } from "../../../../hooks/useToast"
import { ClientPaymentRequests } from "../../../../api/app.service"

const PaymentCenterPage: React.FC = () => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const { show } = useIntercom()
  const { state } = useLocation()
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [loadingPaymentMethods, setLoadingPaymentMethods] =
    useState<boolean>(false)
  const [showDetails, setShowDetails] = useState<boolean>(false)
  const [localPaymentMethods, setLocalPaymentMethods] = useState<any[]>([])
  const [paypalStatus, setPaypalStatus] = useState<any>(null)
  const passedPaymentMethods = state?.localPaymentMethods
  const navigate = useNavigate()

  const goToGetPaid = () => {
    navigate("/coach/client-payments")
  }

  const getPaymentMethods = async () => {
    if (!user) return
    setLoadingPaymentMethods(true)
    try {
      const data = await PaymentRequests.getSavedPaymentMethods({
        user_id: parseInt(user.user_id),
      })

      const allCards = (Array.isArray(data?.data) ? data.data : [])
        .flatMap((entry: any) => entry?.payment_tokens || [])
        .filter((token: any) => token?.payment_source?.card)
        .map((token: any) => ({
          id: token.id,
          payment_method_token: token.id,
          ...token.payment_source.card,
        }))

      setPaymentMethods(allCards)
      setLocalPaymentMethods(allCards)
    } catch (ex: any) {
      console.error("Error fetching payment methods:", ex)
      toast.error(
        ex.response?.data?.message || "Failed to load payment methods."
      )
    } finally {
      setLoadingPaymentMethods(false)
    }
  }

  const getPaypalOnboardingStatus = async () => {
    if (user) {
      try {
        const onboardingStatus =
          await ClientPaymentRequests.getPaypalOnboardingStatus({
            seller_payer_id: null,
            seller_user_id: parseInt(user?.user_id),
          })
        setPaypalStatus(onboardingStatus)
      } catch (error) {
        toast.error(
          "An error occurred trying to fetch your PayPal onboarding status"
        )
      }
    }
  }

  useEffect(() => {
    if (passedPaymentMethods && passedPaymentMethods.length > 0) {
      setPaymentMethods(passedPaymentMethods)
      setLocalPaymentMethods(passedPaymentMethods)
      return
    }

    getPaymentMethods()
  }, [user, passedPaymentMethods])

  useEffect(() => {
    getPaypalOnboardingStatus()
  }, [])

  return (
    <div className="mx-auto flex w-full flex-col px-[20px] xl:w-[1280px] ">
      <div className="mb-4 flex w-full items-center justify-between k">
        <h1 className="flex text-[36px] font-bold w-full">
          Payment Center{" "}
          <Button
            onClick={() => setShowDetails(true)}
            variant="link"
            size="icon"
            className="md:flex"
          >
            <SvgFilledInformationCircle className="w-4 h-4" />
          </Button>
        </h1>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-center w-[100%]  ">
        <div className="flex items-center justify-start justify-start gap-3 text-graySlate text-base font-bold overflow-x-visible w-full md:w-auto mb-[16px] md:mb-0 mr-auto md:mr-0 relative">
          <Button
            size="lg"
            className="z-10 sm:px-6 px-3" 
            variant="disable"
            disabled
          >
            <SvgWallet /> Payment Center
          </Button>

          {user?.isCoach && (
            <div className="relative">
              <Button
                size="lg"
                onClick={goToGetPaid}
                className="z-10 relative sm:px-6 px-3"
                variant="outline"
              >
                <SvgBilling /> Get Paid
              </Button>

              {!paypalStatus?.data?.onboarding_completed && (
                <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full border-2 border-vividOrange flex items-center justify-center z-10 bg-white cursor-help group">
                  <span className="text-vividOrange text-[12px] font-bold leading-none">
                    !
                  </span>

                  <div
                    className="absolute bottom-full transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                     bg-white border border-vividOrange text-vividOrange rounded-md shadow-md px-3 py-2 text-[14px] font-medium w-[240px] text-center z-20"
                  >
                    Finish your PayPal setup to start receiving payments.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center w-full h-auto text-center">
        <SavedCards
          localPaymentMethods={localPaymentMethods}
          setLocalPaymentMethods={setLocalPaymentMethods}
          loading={loadingPaymentMethods}
          user={user}
          getPaymentMethods={getPaymentMethods}
        />
      </div>

      <div className="mb-12 p-6">
        <h2 className="text-left text-[22px] mb-6 font-bold">
          Transaction History
        </h2>

        <TransactionSection localPaymentMethods={localPaymentMethods} />
      </div>

      {showDetails && <PaymentCenterDetails setShowDetails={setShowDetails} />}

      <div className="text-center mb-12" onClick={show}>
        For any questions regarding your payments, please contact{" "}
        <span className="text-blurple cursor-pointer font-bold">Support</span>
      </div>
    </div>
  )
}

export default PaymentCenterPage
